/**
 * List-default tiers — workspace + board columns (FRIDAY-43).
 *
 * The API already exists (FRIDAY-42): one row per tier in
 * `list_view_preferences`, selected by the scope ids in the URL. This module
 * is the only place that knows those wire shapes, so the workspace and board
 * settings screens talk to the same hooks.
 *
 * Two rules the screens depend on:
 *   - a PUT always carries the version that was READ (never a hardcoded
 *     number), so the server can reject a stale write with 409;
 *   - `fields: null` is a tombstone meaning "inherit", not a delete.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ListField } from '@friday/shared/listFields'
import type { Project } from '@friday/shared/realtime'

/** One tier's own state. `fields` is null when the tier has no row. */
export interface ListPreferenceLayer {
  fields: ListField[] | null
  version: number
}

export interface ListPreferenceLayers {
  personal: ListPreferenceLayer
  board: ListPreferenceLayer
  workspace: ListPreferenceLayer
}

export interface ListDefaultsPayload {
  scope: 'workspace' | 'board'
  /** The tier's OWN fields — null when the tier inherits. */
  fields: ListField[] | null
  /** The tier's OWN version — the token a write has to quote. */
  version: number
  layers: ListPreferenceLayers
}

export interface SaveListDefaultsInput {
  /** null = reset to inherit (tombstone). */
  fields: ListField[] | null
  version: number
}

/** Workspace tier — read. */
export function useWorkspaceListDefaults(wsId: string | undefined) {
  return useQuery({
    queryKey: ['workspace-list-defaults', wsId],
    queryFn: () => api<ListDefaultsPayload>(`/api/v1/workspaces/${wsId}/list-preferences`),
    enabled: !!wsId,
  })
}

/** Workspace tier — write. Owner/admin only (server enforced). */
export function useSaveWorkspaceListDefaults(wsId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveListDefaultsInput) =>
      api<{ version: number }>(`/api/v1/workspaces/${wsId}/list-preferences`, {
        method: 'PUT',
        body: { fields: input.fields, version: input.version },
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      // Prefix match — the personal ListView query lives under this key too,
      // so a workspace default change refetches the open list view.
      void qc.invalidateQueries({ queryKey: ['list-preferences'] })
      // Returned (awaited) so the mutation stays pending until the saved row
      // is back: the settings screen drops its draft only then, and its
      // controls stay locked meanwhile, so it never flashes (or lets the user
      // edit) the pre-save server order.
      return qc.invalidateQueries({ queryKey: ['workspace-list-defaults', wsId] })
    },
  })
}

/** Board tier — read (the board's own row, not the effective layout). */
export function useBoardListDefaults(pId: string | undefined) {
  return useQuery({
    queryKey: ['board-list-defaults', pId],
    queryFn: () => api<ListDefaultsPayload>(`/api/v1/projects/${pId}/list-preferences/shared`),
    enabled: !!pId,
  })
}

/** Board tier — write. Project admin only (server enforced). */
export function useSaveBoardListDefaults(pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveListDefaultsInput) =>
      api<{ version: number }>(`/api/v1/projects/${pId}/list-preferences/shared`, {
        method: 'PUT',
        body: { fields: input.fields, version: input.version },
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['list-preferences'] })
      // Awaited for the same reason as the workspace tier above.
      return qc.invalidateQueries({ queryKey: ['board-list-defaults', pId] })
    },
  })
}

/** Board identity — name / colour / icon. Keys are immutable, so they are
 * never part of the patch. */
export interface ProjectIdentityPatch {
  name?: string
  color?: string
  icon?: string
}

export interface UpdateProjectIdentityInput {
  patch: ProjectIdentityPatch
  /** The project version the edit started from — a newer server row is a 409. */
  version: number
}

export function useUpdateProjectIdentity(pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ patch, version }: UpdateProjectIdentityInput) =>
      api<Project>(`/api/v1/projects/${pId}?version=${version}`, { method: 'PATCH', body: patch }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', pId] })
      void qc.invalidateQueries({ queryKey: ['workspaces'] })
      // `features/projects/board.ts` keys the board snapshot by
      // ['board', wsId, pId]; this hook only knows the project id, so the
      // whole board prefix is invalidated.
      void qc.invalidateQueries({ queryKey: ['board'] })
      // The board header and the settings screen resolve the project by key.
      void qc.invalidateQueries({ queryKey: ['project-by-key'] })
      // The workspace overview cards show the board name and colour too.
      void qc.invalidateQueries({ queryKey: ['workspace-overview'] })
    },
  })
}
